// Lector de .env para importar las variables de un fichero que alguien pega o sube al panel. Es el mismo formato que
// admite la CLI (`sgc run --env-file`, cli/src/core/dotenv.js): KEY=valor, `export KEY=valor`, comentarios con #,
// comillas simples (literales) y dobles (con \n \r \t \" \\ y saltos de linea reales), comentario al final de un
// valor sin comillas (` # ...`), CRLF y BOM. Sin interpolacion: el valor es lo que hay escrito.
//
// Diferencia con la CLI: aqui una linea mala no aborta; se salta y se devuelve en `errors` (con su numero de linea y
// un codigo, nunca con el valor) para importar lo que si es valido.

export const ENV_KEY_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * @param {string} text
 * @returns {{ entries: Array<{ key: string, value: string, line: number }>, errors: Array<{ line: number, code: string }> }}
 */
export function parseDotenv(text) {
  const src = String(text ?? '').replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
  const entries = [];
  const errors = [];
  const index = new Map();
  let i = 0;
  let line = 1;

  const nextLineStart = (from) => { const nl = src.indexOf('\n', from); return nl === -1 ? src.length : nl + 1; };

  while (i < src.length) {
    const eol = src.indexOf('\n', i);
    const rawLine = src.slice(i, eol === -1 ? src.length : eol);
    const trimmed = rawLine.trim();
    if (trimmed === '' || trimmed.startsWith('#')) {
      i = nextLineStart(i);
      line += 1;
      continue;
    }
    const startLine = line;
    const m = /^\s*(?:export\s+)?([^\s=]+)\s*=\s*/.exec(rawLine);
    if (!m) { errors.push({ line: startLine, code: 'expected_key_value' }); i = nextLineStart(i); line += 1; continue; }
    const key = m[1];
    if (!ENV_KEY_RE.test(key)) { errors.push({ line: startLine, code: 'invalid_key' }); i = nextLineStart(i); line += 1; continue; }

    const pos = i + m[0].length;
    const q = src[pos];
    let value;
    if (q === '"' || q === "'") {
      let j = pos + 1;
      let out = '';
      let closed = false;
      let lines = 0;
      while (j < src.length) {
        const ch = src[j];
        if (q === '"' && ch === '\\' && j + 1 < src.length) {
          const next = src[j + 1];
          out += next === 'n' ? '\n' : next === 'r' ? '\r' : next === 't' ? '\t' : next;
          j += 2;
          continue;
        }
        if (ch === q) { closed = true; j += 1; break; }
        if (ch === '\n') lines += 1;
        out += ch;
        j += 1;
      }
      if (!closed) {
        // Una comilla sin cerrar se come el resto del fichero: se salta solo esta linea y se sigue.
        errors.push({ line: startLine, code: 'unterminated_quote' });
        i = nextLineStart(i);
        line += 1;
        continue;
      }
      const restEnd = src.indexOf('\n', j) === -1 ? src.length : src.indexOf('\n', j);
      const rest = src.slice(j, restEnd).trim();
      if (rest !== '' && !rest.startsWith('#')) {
        errors.push({ line: startLine, code: 'text_after_quote' });
        i = nextLineStart(j);
        line += lines + 1;
        continue;
      }
      value = out;
      i = nextLineStart(j);
      line += lines + 1;
    } else {
      const rest = rawLine.slice(m[0].length);
      const hash = rest.search(/\s#/);
      value = (hash === -1 ? rest : rest.slice(0, hash)).trim();
      i = nextLineStart(i);
      line += 1;
    }

    const entry = { key, value, line: startLine };
    if (index.has(key)) entries[index.get(key)] = entry; // la ultima definicion gana, como dotenv
    else { index.set(key, entries.length); entries.push(entry); }
  }
  return { entries, errors };
}

/** Un valor para una linea de .env: tal cual si es seguro; si no, entre comillas simples o, si tiene saltos o comillas simples, dobles con escapes. */
export function dotenvValue(value) {
  const v = String(value ?? '');
  const singleOk = !/['\r\n]/.test(v);
  if (v !== '' && !/[\s#"'\\$`]/.test(v)) return v;
  if (singleOk) return `'${v}'`;
  return `"${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\r/g, '\\r').replace(/\n/g, '\\n')}"`;
}
